<template>
  <div class="perfil-page">
    <div class="modern-header">
      <div class="header-container">
        <div class="breadcrumb-section">
          <nav class="breadcrumb">
            <button type="button" class="breadcrumb-item" @click="$router.push('/dashboard')">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              {{ t('common.breadcrumb.dashboard') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ t('perfil.breadcrumb') }}</span>
          </nav>
        </div>

        <div class="title-section">
          <div class="title-content">
            <div class="title-icon title-icon--profile">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A8 8 0 1118.88 17.8M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <div class="title-text">
              <h1 class="main-title">{{ t('perfil.title') }}</h1>
              <p class="subtitle">{{ t('perfil.subtitle') }}</p>
            </div>
          </div>

          <div v-if="meuPlano?.nome" class="workspace-badge badge-plan">
            <div class="badge-icon badge-icon--plan" aria-hidden="true">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
              </svg>
            </div>
            <span class="badge-text">{{ meuPlano.nome }}</span>
          </div>
        </div>

        <div class="actions-section">
          <button
            type="button"
            class="action-button secondary"
            :disabled="!canFullEdit || savingPwd"
            @click="openPwdModal"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            {{ t('perfil.changePassword') }}
          </button>
          <button type="button" class="action-button primary" @click="$router.push('/upgrade')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
            </svg>
            {{ t('perfil.viewPlans') }}
          </button>
        </div>
      </div>
    </div>

    <div class="perfil-content">
      <div class="perfil-container">
      <div v-if="!authStore.user && loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>{{ t('perfil.loading') }}</p>
      </div>

      <div v-else class="grid-layout">
        <div class="left-col">
          <div class="premium-card profile-identity-card">
            <div class="profile-identity-glow" aria-hidden="true" />
            <div class="user-head">
              <div class="avatar-wrap">
                <img :src="resolvedPreviewFoto" class="avatar-image" :alt="t('perfil.avatarAlt')" />
              </div>
              <div class="user-head__info">
                <h3 class="user-name">{{ form.nomeCompleto || authStore.user?.nomeCompleto || t('perfil.userFallback') }}</h3>
                <p class="user-email">{{ form.email || authStore.user?.email || '' }}</p>
                <div class="user-status">
                  <span class="status-dot"></span>
                  <span>{{ t('perfil.online') }}</span>
                </div>
              </div>
            </div>

            <div class="meta-grid">
              <div class="meta-item">
                <span class="meta-item__icon" aria-hidden="true">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                </span>
                <p class="meta-label">{{ t('perfil.memberSince') }}</p>
                <p class="meta-value">{{ formatDate(memberSinceDate) }}</p>
              </div>
              <div class="meta-item">
                <span class="meta-item__icon" aria-hidden="true">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                </span>
                <p class="meta-label">{{ t('perfil.lastUpdate') }}</p>
                <p class="meta-value">{{ formatDate(lastUpdateDate) }}</p>
              </div>
            </div>

            <div class="plan-banner">
              <div class="plan-banner__icon" aria-hidden="true">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>
              </div>
              <div class="plan-banner__text">
                <span class="plan-banner__label">{{ t('perfil.activePlan') }}</span>
                <span class="plan-banner__value">{{ meuPlano?.nome || t('perfil.planFree') }}</span>
              </div>
            </div>
          </div>

          <div class="premium-card panel-card">
            <header class="panel-card__header">
              <div class="panel-card__icon panel-card__icon--photo">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              </div>
              <div>
                <h4 class="card-title">{{ t('perfil.photoTitle') }}</h4>
                <p class="panel-card__subtitle">{{ t('perfil.photoSubtitle') }}</p>
              </div>
            </header>
            <div class="photo-accordion">
              <div class="accordion-item" :class="{ open: fotoType === 'avatar' }">
                <button class="accordion-trigger" type="button" @click="setFotoType('avatar')">
                  <span>{{ t('perfil.avatars') }}</span>
                  <span class="accordion-hint">{{ t('perfil.avatarsHint') }}</span>
                </button>
                <div v-if="fotoType === 'avatar'" class="accordion-body">
                  <div class="avatars-grid">
                    <button
                      v-for="avatar in avatars"
                      :key="avatar"
                      type="button"
                      class="avatar-btn"
                      :class="{ selected: selectedAvatar === avatar }"
                      :disabled="saving || !canFullEdit"
                      @click="selectAvatar(avatar)"
                    >
                      <img :src="avatar" :alt="t('perfil.avatarAlt')" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="accordion-item" :class="{ open: fotoType === 'upload', disabled: !canFullEdit }">
                <button class="accordion-trigger" type="button" @click="setFotoType('upload')" :disabled="!canFullEdit">
                  <span>{{ t('perfil.upload') }}</span>
                  <span class="accordion-hint">{{ t('perfil.uploadHint') }}</span>
                </button>
                <div v-if="fotoType === 'upload'" class="accordion-body">
                  <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileSelect"
                    accept="image/*"
                    class="hidden-input"
                    :disabled="!canFullEdit || saving"
                  />
                  <button
                    class="upload-btn"
                    type="button"
                    :disabled="!canFullEdit || saving"
                    @click="triggerFileSelect"
                  >
                    {{ t('perfil.uploadPhoto') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="right-col">
          <div class="premium-card panel-card">
            <header class="panel-card__header">
              <div class="panel-card__icon panel-card__icon--info">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
              </div>
              <div>
                <h4 class="card-title">{{ t('perfil.personalInfo') }}</h4>
                <p class="panel-card__subtitle">{{ t('perfil.personalInfoSubtitle') }}</p>
              </div>
            </header>
            <div class="form-stack">
            <label class="input-label">{{ t('perfil.fullName') }}</label>
            <input
              v-model="form.nomeCompleto"
              class="input-field"
              type="text"
              :disabled="!canFullEdit || saving"
            />

            <label class="input-label">{{ t('common.email') }}</label>
            <input
              v-model="form.email"
              class="input-field"
              type="email"
              :disabled="!canFullEdit || saving"
            />

            <button class="btn-primary btn-save" :disabled="saving || !canFullEdit" @click="salvarPerfil">
              <svg v-if="!saving" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
              {{ saving ? t('common.saving') : t('perfil.saveChanges') }}
            </button>
            </div>
          </div>

          <div class="premium-card panel-card panel-card--security">
            <header class="panel-card__header">
              <div class="panel-card__icon panel-card__icon--security">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
              </div>
              <div>
                <h4 class="card-title">{{ t('perfil.security') }}</h4>
                <p class="panel-card__subtitle">{{ t('perfil.securitySubtitle') }}</p>
              </div>
            </header>
            <p class="security-note">
              {{ t('perfil.securityNote') }}
            </p>
            <button
              class="btn-outline-danger"
              type="button"
              :disabled="!canFullEdit || savingPwd"
              @click="openPwdModal"
            >
              {{ t('perfil.changePassword') }}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>

    <teleport to="body">
      <div v-if="showUploader" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
        <div class="bg-discord-dark-300 rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <ImageUploadCrop
            :selected-file="selectedFile"
            @image-uploaded="onImageUploaded"
            @close="closeUploader"
          />
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showPwdModal"
        class="fixed inset-0 z-[70] flex items-center justify-center px-4 pwd-modal-backdrop"
        @click.self="closePwdModal"
      >
        <div class="pwd-modal-shell" @click.stop>
          <div class="pwd-modal-accent" />
          <div class="pwd-modal-body">
            <div class="pwd-modal-head">
              <div class="pwd-modal-icon" aria-hidden="true">
                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div>
                <h4 class="card-title pwd-modal-title">{{ t('perfil.pwdModalTitle') }}</h4>
                <p class="pwd-modal-sub">
                  {{ t('perfil.pwdModalSub') }}
                </p>
              </div>
            </div>

            <form class="pwd-modal-form" @submit.prevent="alterarSenha">
              <PasswordFieldsBlock
                id-prefix="meu-perfil-pwd"
                :senha-atual="pwd.senhaAtual"
                :nova-senha="pwd.novaSenha"
                :confirmar-nova-senha="pwd.confirmarNovaSenha"
                :disabled="savingPwd"
                @update:senha-atual="(v) => (pwd.senhaAtual = v)"
                @update:nova-senha="(v) => (pwd.novaSenha = v)"
                @update:confirmar-nova-senha="(v) => (pwd.confirmarNovaSenha = v)"
              />

              <p v-if="!canSubmitPwd && !savingPwd" class="pwd-modal-hint">
                {{ t('perfil.pwdModalHint') }}
              </p>

              <div class="pwd-modal-actions">
                <button type="button" class="btn-secondary" :disabled="savingPwd" @click="closePwdModal">
                  {{ t('common.cancel') }}
                </button>
                <button type="submit" class="btn-danger" :disabled="savingPwd || !canSubmitPwd">
                  {{ savingPwd ? t('common.saving') : t('common.confirm') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { formatDate as formatDateIntl } from '../utils/format'
import userApi from '../services/user'
import api from '../services/api'
import ImageUploadCrop from '../components/ImageUploadCrop.vue'
import PasswordFieldsBlock from '../components/PasswordFieldsBlock.vue'
import { getFileUrl, joinUrl, config } from '../config/app'
import { useToast } from 'vue-toastification'

const authStore = useAuthStore()
const toast = useToast()
const { t, locale } = useI18n()
const saving = ref(false)
const savingPwd = ref(false)
const loading = ref(true)
const nowFallback = ref(new Date().toISOString())
const fileInput = ref(null)
const selectedAvatar = ref(null)
const uploadedFoto = ref(null)
const showUploader = ref(false)
const showPwdModal = ref(false)
const selectedFile = ref(null)
const fotoType = ref('avatar')
const meuPlano = ref(null)
const form = ref({
  nomeCompleto: '',
  email: '',
  fotoPerfil: ''
})
const pwd = ref({
  senhaAtual: '',
  novaSenha: '',
  confirmarNovaSenha: ''
})

const canSubmitPwd = computed(() => {
  const atual = pwd.value.senhaAtual?.trim()
  const nova = (pwd.value.novaSenha || '').trim()
  const conf = pwd.value.confirmarNovaSenha || ''
  return (
    !!atual &&
    nova.length >= 6 &&
    conf.length > 0 &&
    pwd.value.novaSenha === pwd.value.confirmarNovaSenha
  )
})

const avatars = Array.from({ length: 35 }, (_, idx) =>
  joinUrl(config.fileServerUrl, `avatars/${idx + 1}.png`)
)

const previewFoto = computed(() => uploadedFoto.value || selectedAvatar.value || form.value.fotoPerfil || '/logo_w.png')
const resolvedPreviewFoto = computed(() => getFileUrl(previewFoto.value) || previewFoto.value)
const canFullEdit = computed(() => true)
const memberSinceDate = computed(() => authStore.user?.dataCriacao || nowFallback.value)
const lastUpdateDate = computed(
  () =>
    authStore.user?.dataAtualizacao ||
    authStore.user?.dataUpdate ||
    authStore.user?.dataCriacao ||
    nowFallback.value
)

const formatDate = (dateString) => {
  const fallback = formatDateIntl(nowFallback.value, locale.value)
  if (!dateString) return fallback
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) {
    return fallback
  }
  return formatDateIntl(date, locale.value)
}

const persistFotoPerfil = async (fotoUrl) => {
  if (!canFullEdit.value) return
  const url = fotoUrl == null ? '' : String(fotoUrl).trim()
  saving.value = true
  try {
    const payload = {
      nomeCompleto: form.value.nomeCompleto,
      email: form.value.email,
      fotoPerfil: url
    }
    const { data } = await userApi.updateMe(payload)
    authStore.updateUser(data)
    form.value.fotoPerfil = data?.fotoPerfil ?? url
    if (url) {
      uploadedFoto.value = form.value.fotoPerfil
    } else {
      uploadedFoto.value = null
      selectedAvatar.value = null
    }
    toast.success(t('perfil.toast.photoUpdated'))
  } catch (e) {
    console.error(e)
    toast.error(t('perfil.toast.photoError'))
  } finally {
    saving.value = false
  }
}

const openPwdModal = () => {
  pwd.value = { senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' }
  showPwdModal.value = true
}

const closePwdModal = () => {
  showPwdModal.value = false
  pwd.value = { senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' }
}

const carregar = async () => {
  loading.value = true
  try {
    const [{ data }, { data: plano }] = await Promise.all([
      userApi.getMe(),
      api.get('/planos/meu-plano')
    ])
    form.value.nomeCompleto = data?.nomeCompleto || ''
    form.value.email = data?.email || ''
    form.value.fotoPerfil = data?.fotoPerfil || ''
    meuPlano.value = plano || null
    authStore.updateUser({ ...(data || {}), planoCodigo: plano?.codigo || data?.planoCodigo })
  } finally {
    loading.value = false
  }
}

const salvarPerfil = async () => {
  if (!canFullEdit.value) return
  saving.value = true
  try {
    const payload = {
      ...form.value,
      fotoPerfil: uploadedFoto.value || selectedAvatar.value || form.value.fotoPerfil
    }
    const { data } = await userApi.updateMe(payload)
    authStore.updateUser(data)
    form.value.fotoPerfil = data?.fotoPerfil || payload.fotoPerfil || ''
    toast.success(t('perfil.toast.profileSaved'))
  } finally {
    saving.value = false
  }
}

const alterarSenha = async () => {
  if (!canFullEdit.value) return
  if (!canSubmitPwd.value) {
    toast.error(t('perfil.toast.pwdValidation'))
    return
  }
  if (pwd.value.novaSenha !== pwd.value.confirmarNovaSenha) {
    toast.error(t('perfil.toast.pwdMismatch'))
    return
  }
  savingPwd.value = true
  try {
    await userApi.updatePassword({
      senhaAtual: pwd.value.senhaAtual,
      novaSenha: pwd.value.novaSenha
    })
    pwd.value.senhaAtual = ''
    pwd.value.novaSenha = ''
    pwd.value.confirmarNovaSenha = ''
    toast.success(t('perfil.toast.passwordChanged'))
    closePwdModal()
  } catch (e) {
    console.error(e)
    toast.error(t('perfil.toast.passwordError'))
  } finally {
    savingPwd.value = false
  }
}

const setFotoType = (type) => {
  if (type === 'upload' && !canFullEdit.value) return
  fotoType.value = type
}

const selectAvatar = async (avatar) => {
  if (!canFullEdit.value) return
  selectedAvatar.value = avatar
  uploadedFoto.value = null
  await persistFotoPerfil(avatar)
}

const triggerFileSelect = () => {
  if (!canFullEdit.value || !fileInput.value) return
  fileInput.value.click()
}

const handleFileSelect = (event) => {
  if (!canFullEdit.value) return
  const file = event.target.files?.[0]
  if (!file) return
  selectedFile.value = file
  showUploader.value = true
  event.target.value = ''
}

const onImageUploaded = async (imageUrl) => {
  if (!canFullEdit.value) return
  uploadedFoto.value = imageUrl
  selectedAvatar.value = null
  form.value.fotoPerfil = imageUrl
  showUploader.value = false
  selectedFile.value = null
  await persistFotoPerfil(imageUrl)
}

const closeUploader = () => {
  showUploader.value = false
  selectedFile.value = null
}

onMounted(carregar)
</script>

<style scoped>
.perfil-page {
  min-height: 100%;
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
  color: var(--text-primary);
}

.modern-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
  margin-bottom: 0;
}

.header-container {
  width: 100%;
  padding: 0 24px;
}

.breadcrumb-section {
  margin-bottom: 16px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.breadcrumb-separator {
  color: var(--text-tertiary);
  font-weight: 500;
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 600;
  padding: 6px 12px;
  background: var(--bg-secondary);
  border-radius: 4px;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 4px;
  color: white;
  flex-shrink: 0;
}

.title-icon--profile {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4);
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

.subtitle {
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 4px 0 0;
}

.workspace-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.badge-plan {
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.35);
  color: #6366f1;
}

.badge-icon--plan {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.actions-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  text-decoration: none;
}

.action-button.secondary {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-primary);
}

.action-button.secondary:hover:not(:disabled) {
  background: var(--bg-primary);
  border-color: color-mix(in srgb, var(--accent-primary) 40%, var(--border-primary));
}

.action-button.primary {
  background: linear-gradient(135deg, #6366f1, #7c3aed);
  color: #fff;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.action-button.primary:hover {
  filter: brightness(1.05);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.45);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.perfil-content {
  width: 100%;
  padding: 28px 24px 40px;
  box-sizing: border-box;
}

.perfil-container {
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.loading-state {
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  border-radius: 4px;
  padding: 56px 40px;
  text-align: center;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.06);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid var(--border-primary);
  border-top-color: var(--accent-primary);
  margin: 0 auto 10px;
  animation: spin 1s linear infinite;
}

.grid-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 22px;
  align-items: start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.left-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.premium-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 16px;
}

.profile-identity-card {
  position: relative;
  overflow: hidden;
  padding: 22px 20px 20px;
  background:
    radial-gradient(ellipse 80% 60% at 100% 0%, color-mix(in srgb, #6366f1 14%, transparent), transparent 55%),
    radial-gradient(ellipse 60% 50% at 0% 100%, color-mix(in srgb, #8b5cf6 10%, transparent), transparent 50%),
    var(--bg-secondary);
  border-color: color-mix(in srgb, #6366f1 18%, var(--border-primary));
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.08);
}

.profile-identity-glow {
  position: absolute;
  top: -40%;
  right: -20%;
  width: 55%;
  height: 120%;
  background: radial-gradient(circle, color-mix(in srgb, #6366f1 16%, transparent), transparent 68%);
  pointer-events: none;
}

.profile-identity-card .user-head {
  position: relative;
  z-index: 1;
}

.plan-banner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 4px;
  background: linear-gradient(135deg, color-mix(in srgb, #6366f1 12%, var(--bg-primary)), var(--bg-primary));
  border: 1px solid color-mix(in srgb, #6366f1 28%, var(--border-primary));
}

.plan-banner__icon {
  width: 36px;
  height: 36px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  flex-shrink: 0;
}

.plan-banner__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.plan-banner__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.plan-banner__value {
  font-size: 15px;
  font-weight: 800;
  color: var(--text-primary);
}

.panel-card {
  padding: 20px 22px 22px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.06);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.panel-card:hover {
  border-color: color-mix(in srgb, #6366f1 22%, var(--border-primary));
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.1);
}

.panel-card__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);
}

.panel-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.panel-card__icon--photo {
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.35);
}

.panel-card__icon--info {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.panel-card__icon--security {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
}

.panel-card__subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.45;
}

.panel-card .card-title {
  margin: 0;
  font-size: 17px;
  letter-spacing: -0.02em;
}

.panel-card--security {
  background: color-mix(in srgb, var(--bg-secondary) 92%, transparent);
}

.form-stack {
  display: flex;
  flex-direction: column;
}

.security-note {
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text-secondary);
}

.meta-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
  border-radius: 4px;
  color: #6366f1;
  background: color-mix(in srgb, #6366f1 12%, var(--bg-primary));
}

.card-title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
}

.user-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 0;
}

.avatar-wrap {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  padding: 3px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.35);
}

.avatar-wrap .avatar-image {
  border-radius: 50%;
  border: 3px solid var(--bg-secondary);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.user-email {
  margin: 2px 0 6px;
  font-size: 13px;
  color: var(--text-secondary);
}

.user-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #16a34a;
  background: color-mix(in srgb, #22c55e 12%, var(--bg-primary));
  border: 1px solid color-mix(in srgb, #22c55e 28%, transparent);
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.65);
}

.meta-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 18px;
}

.meta-item {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 14px 12px;
  text-align: center;
  background: color-mix(in srgb, var(--bg-primary) 88%, transparent);
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.meta-item:hover {
  border-color: color-mix(in srgb, #6366f1 30%, var(--border-primary));
  transform: translateY(-2px);
}

.meta-label {
  margin: 0;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--text-secondary);
  letter-spacing: 0.7px;
}

.meta-value {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
}

.current-plan-row {
  margin-top: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.plan-chip-label {
  font-size: 11px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.plan-chip-value {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 4px 8px;
  background: color-mix(in srgb, var(--accent-primary) 12%, var(--bg-primary));
}

.plan-warning {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.34);
  color: #fca5a5;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 12px;
}

.photo-accordion {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.accordion-item {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  overflow: hidden;
}

.accordion-item.open {
  border-color: color-mix(in srgb, var(--accent-primary) 45%, var(--border-primary));
}

.accordion-item.disabled {
  opacity: 0.6;
}

.accordion-trigger {
  width: 100%;
  border: none;
  background: color-mix(in srgb, var(--bg-primary) 70%, transparent);
  color: var(--text-primary);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: background 0.2s ease, color 0.2s ease;
}

.accordion-item.open .accordion-trigger {
  background: color-mix(in srgb, #6366f1 10%, var(--bg-primary));
  color: color-mix(in srgb, #6366f1 85%, var(--text-primary));
}

.accordion-trigger:disabled {
  cursor: not-allowed;
}

.accordion-hint {
  font-size: 11px;
  color: var(--text-secondary);
}

.accordion-body {
  border-top: 1px solid var(--border-primary);
  padding: 12px;
}

.avatars-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 4px;
}

.avatar-btn {
  border: 2px solid var(--border-primary);
  border-radius: 999px;
  overflow: hidden;
  width: 42px;
  height: 42px;
  padding: 0;
  background: transparent;
  cursor: pointer;
  justify-self: center;
}

.avatar-btn.selected {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px color-mix(in srgb, #6366f1 25%, transparent);
  transform: scale(1.06);
}

.avatar-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.avatar-btn img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hidden-input {
  display: none;
}

.upload-btn {
  width: 100%;
  padding: 14px 16px;
  border-radius: 4px;
  border: 2px dashed color-mix(in srgb, #6366f1 35%, var(--border-primary));
  background: color-mix(in srgb, #6366f1 6%, var(--bg-primary));
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-btn:hover:not(:disabled) {
  border-color: #6366f1;
  background: color-mix(in srgb, #6366f1 12%, var(--bg-primary));
}

.upload-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.input-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin: 14px 0 8px;
}

.form-stack .input-label:first-child {
  margin-top: 0;
}

.input-field {
  width: 100%;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 12px 14px;
  margin-bottom: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-field:focus {
  outline: none;
  border-color: color-mix(in srgb, #6366f1 55%, var(--border-primary));
  box-shadow: 0 0 0 3px color-mix(in srgb, #6366f1 18%, transparent);
}

.input-field:disabled {
  opacity: 0.6;
}

.btn-outline-danger {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #ef4444;
  background: color-mix(in srgb, #ef4444 8%, var(--bg-primary));
  border: 1px solid color-mix(in srgb, #ef4444 35%, var(--border-primary));
  transition: all 0.2s ease;
}

.btn-outline-danger:hover:not(:disabled) {
  background: color-mix(in srgb, #ef4444 14%, var(--bg-primary));
  border-color: #ef4444;
}

.btn-save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  border-radius: 4px;
  background: linear-gradient(135deg, #6366f1, #7c3aed);
  box-shadow: 0 6px 18px rgba(99, 102, 241, 0.35);
  transition: filter 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.btn-save:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.45);
  transform: translateY(-1px);
}

.btn-primary,
.btn-danger,
.btn-secondary {
  width: 100%;
  border: none;
  border-radius: 4px;
  color: #fff;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 6px;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.pwd-modal-actions .btn-primary,
.pwd-modal-actions .btn-danger,
.pwd-modal-actions .btn-secondary {
  width: auto;
  flex: 1;
  margin-top: 0;
}

.pwd-modal-backdrop {
  background: color-mix(in srgb, var(--bg-primary) 42%, black);
  backdrop-filter: blur(2px);
}

.pwd-modal-shell {
  position: relative;
  max-width: 440px;
  width: 100%;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid var(--border-secondary);
  background: var(--bg-card);
  box-shadow: 0 25px 50px -12px color-mix(in srgb, var(--bg-primary) 50%, transparent);
}

.pwd-modal-accent {
  height: 4px;
  width: 100%;
  background: linear-gradient(90deg, var(--accent-primary), var(--info), var(--success));
}

.pwd-modal-body {
  padding: 1.25rem 1.5rem 1.5rem;
}

.pwd-modal-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.pwd-modal-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--accent-primary) 22%, transparent);
  color: var(--accent-primary);
}

.pwd-modal-title {
  margin: 0 0 4px;
}

.pwd-modal-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--text-secondary);
}

.pwd-modal-form {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pwd-modal-hint {
  margin: 8px 0 0;
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
}

.pwd-modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.btn-primary {
  background: var(--accent-primary, #5865f2);
}

.btn-danger {
  background: var(--error, #dc2626);
}

.btn-primary:disabled,
.btn-danger:disabled,
.btn-secondary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .title-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .grid-layout {
    grid-template-columns: 1fr;
  }

  .avatars-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .main-title {
    font-size: 1.5rem;
  }

  .actions-section {
    width: 100%;
  }

  .action-button {
    flex: 1;
    justify-content: center;
  }
}
</style>
